import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link }                    from "react-router-dom";
import { doc, getDoc, setDoc, updateDoc, addDoc, collection, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { db, auth }                from "../firebase";
import { useAuth }                 from "../hooks/useAuth";
import { useRol }                  from "../hooks/useRoles";
import { useClientes }             from "../hooks/useClientes";
import { useCotizaciones }         from "../hooks/useCotizaciones";
import { usePedidos }              from "../hooks/usePedidos";
import { useReuniones }            from "../hooks/useReuniones";
import { useRecompra }             from "../hooks/useRecompra";
import { useMiDia }                from "../hooks/useMiDia";
import { useTareasManuales }       from "../hooks/useTareasManuales";
import { guardarInteraccion, getMemoriaCliente } from "../hooks/useMemoriaAgente";
import { crearActividadCliente }   from "../hooks/useActividadesCliente";
import { abrirWAConGuion }         from "../utils/whatsapp";
import { fechaHoyLocal }           from "../utils/fechas";
import toast                       from "react-hot-toast";

// ── Constantes de estilo ─────────────────────────────────────────────────────
const PRIORIDAD = {
  urgente:    { bg: "#FEE2E2", color: "#DC2626", borde: "#DC2626", label: "URGENTE" },
  importante: { bg: "#FEF3C7", color: "#B45309", borde: "#F59E0B", label: "IMPORTANTE" },
  normal:     { bg: "#F0FDF4", color: "#15803D", borde: "#22C55E", label: "NORMAL" },
};
const TIPO_ICON  = { seguimiento: "📞", cotizacion: "📋", llamada: "📱", visita: "🏗", manual: "✏️" };
const TIPO_MAP   = { seguimiento: "Llamada", llamada: "Llamada", cotizacion: "Llamada", visita: "Visita en obra", manual: "Llamada" };

const SUGERENCIAS_CHAT = [
  "¿Qué hago si no me contesta?",
  "¿Cómo manejo la objeción de precio?",
  "Reescribe el guión más conciso",
  "Dame argumentos de valor para esta obra",
];

const INTENCION_LABEL = {
  objecion_precio:           { texto: "💰 Objeción de precio",     bg: "#FEF3C7", color: "#B45309" },
  cliente_no_responde:       { texto: "📵 Cliente sin respuesta",  bg: "#FEE2E2", color: "#DC2626" },
  reescribir_guion:          { texto: "✍️ Guión / Mensaje",         bg: "#EEF2FF", color: "#3730A3" },
  cierre:                    { texto: "🤝 Cierre de venta",         bg: "#F0FDF4", color: "#15803D" },
  competencia:               { texto: "⚔️ Vs. competencia",         bg: "#FDF4FF", color: "#7C3AED" },
  general:                   { texto: "💬 Consulta general",        bg: "#F1F5F9", color: "#475569" },
  escalacion_precio_actual:  { texto: "⚠️ Requiere área comercial", bg: "#FEF3C7", color: "#92400E" },
  escalacion_credito:        { texto: "⚠️ Requiere área de créditos", bg: "#FEF3C7", color: "#92400E" },
  escalacion_reclamo:        { texto: "⚠️ Requiere operaciones",    bg: "#FEF3C7", color: "#92400E" },
  escalacion_tecnico:        { texto: "⚠️ Requiere asesor técnico", bg: "#FEF3C7", color: "#92400E" },
};

// ── Utilidad de fecha local ───────────────────────────────────────────────────
function etiquetaFechaSeguimiento(fechaStr) {
  if (!fechaStr) return null;
  const [y, m, d] = String(fechaStr).split("-").map(Number);
  if (!y || !m || !d) return null;
  const hoy  = new Date(); hoy.setHours(0, 0, 0, 0);
  const fecha = new Date(y, m - 1, d);
  const dias  = Math.round((fecha - hoy) / 86400000);
  if (dias <= -2) return { texto: `Vencido hace ${Math.abs(dias)}d`, bg: "#FEE2E2", color: "#DC2626" };
  if (dias === -1) return { texto: "Venció ayer",                    bg: "#FEE2E2", color: "#DC2626" };
  if (dias === 0)  return { texto: "Hoy",                            bg: "#FEF3C7", color: "#B45309" };
  if (dias === 1)  return { texto: "Mañana",                         bg: "#FEF9C3", color: "#92400E" };
  return               { texto: `En ${dias} días`,                   bg: "#F0FDF4", color: "#15803D" };
}

function proximoEnDias(dias) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// ─── Helper: parsea "Mi enfoque: X\nRespuesta..." ────────────────────────────
function parsearRespuesta(texto) {
  const match = texto.match(/^Mi enfoque:\s*([^\n]+)\n+([\s\S]+)/i);
  if (match) return { enfoque: match[1].trim(), cuerpo: match[2].trim() };
  return { enfoque: null, cuerpo: texto };
}

// Convierte *cursiva*, **negrita**, ***negrita+cursiva*** dentro de una línea
function formatearInline(texto, lineKey) {
  const re = /(\*{1,3})(.+?)\1/g;
  const partes = [];
  let ultimo = 0, m, idx = 0;
  while ((m = re.exec(texto)) !== null) {
    if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
    const n = m[1].length, t = m[2];
    if (n === 3)      partes.push(<strong key={`${lineKey}-${idx++}`} style={{ fontWeight: 700 }}><em>{t}</em></strong>);
    else if (n === 2) partes.push(<strong key={`${lineKey}-${idx++}`} style={{ fontWeight: 700 }}>{t}</strong>);
    else              partes.push(<em     key={`${lineKey}-${idx++}`}>{t}</em>);
    ultimo = m.index + m[0].length;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes.length > 0 ? partes : texto;
}

// Renderiza el cuerpo del mensaje sin asteriscos ni guiones crudos
function renderMensaje(texto) {
  if (!texto) return null;
  return texto.split('\n').map((linea, i) => {
    // Separador --- → línea horizontal
    if (/^-{3,}$/.test(linea.trim()))
      return <hr key={i} style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "5px 0" }} />;
    // Línea vacía → espacio
    if (!linea.trim())
      return <div key={i} style={{ height: 5 }} />;
    // Bullet - item o * item
    const bullet = linea.match(/^[\-\*•]\s+(.*)/);
    const contenido = formatearInline(bullet ? bullet[1] : linea, i);
    if (bullet)
      return (
        <div key={i} style={{ display: "flex", gap: 5, marginBottom: 2 }}>
          <span style={{ flexShrink: 0, marginTop: 1 }}>•</span>
          <span>{contenido}</span>
        </div>
      );
    return <div key={i}>{contenido}</div>;
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Chat con el agente por tarea — con historial persistente en Firestore
// ═══════════════════════════════════════════════════════════════════════════════
function ChatAgente({ tarea, nombreUsuario, clienteCompleto, cotizacionesCliente }) {
  const { user }   = useAuth();
  const userId     = user?.uid;
  const clienteId  = tarea.clienteId;

  const [abierto,        setAbierto]        = useState(false);
  const [historial,      setHistorial]      = useState([]);
  const [memoriaReal,    setMemoriaReal]    = useState([]);
  const [pregunta,       setPregunta]       = useState("");
  const [enviando,       setEnviando]       = useState(false);
  const [cargando,       setCargando]       = useState(false);
  const [feedbacks,      setFeedbacks]      = useState({}); // { msgIndex: "util" | "no_util" }
  const endRef = useRef(null);
  const cargadoRef = useRef(false);

  // Ref de Firestore para el chat de este cliente
  const chatDocRef = useCallback(() => {
    if (!userId || !clienteId) return null;
    return doc(db, "chatAgente", `${userId}_${clienteId}`);
  }, [userId, clienteId]);

  // Al abrir: cargar historial persistido + interacciones reales del cliente
  useEffect(() => {
    if (!abierto || cargadoRef.current) return;
    cargadoRef.current = true;
    setCargando(true);

    const ref = chatDocRef();
    const promesas = [
      ref ? getDoc(ref).then(snap => snap.exists() ? (snap.data().historial || []) : []).catch(() => []) : Promise.resolve([]),
      clienteId && userId ? getMemoriaCliente(userId, clienteId) : Promise.resolve([]),
    ];

    Promise.all(promesas).then(([chatGuardado, memoria]) => {
      setHistorial(chatGuardado);
      setMemoriaReal(memoria);
    }).finally(() => setCargando(false));
  }, [abierto, chatDocRef, clienteId, userId]);

  // Scroll automático al último mensaje
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [historial, enviando]);

  // Guardar historial en Firestore (máx 40 mensajes = 20 intercambios)
  const persistir = useCallback((nuevoHistorial) => {
    const ref = chatDocRef();
    if (!ref) return;
    setDoc(ref, {
      userId,
      clienteId,
      clienteNombre:  tarea.clienteNombre || "",
      historial:      nuevoHistorial.slice(-40),
      actualizadoEn:  serverTimestamp(),
    }).catch(() => {});
  }, [chatDocRef, userId, clienteId, tarea.clienteNombre]);

  const enviar = useCallback(async (texto) => {
    const msg = (texto || pregunta).trim();
    if (!msg || enviando) return;
    setPregunta("");

    const conMensaje = [...historial, { rol: "user", texto: msg, fecha: fechaHoyLocal() }];
    setHistorial(conMensaje);
    setEnviando(true);

    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/chat-agente", {
        method:  "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          tarea,
          historial:           historial.map(h => ({ rol: h.rol, texto: h.texto })),
          memoriaCliente:      memoriaReal,
          pregunta:            msg,
          nombreUsuario,
          clienteCompleto:     clienteCompleto ? {
            ruc:               clienteCompleto.ruc          || "",
            telefono:          clienteCompleto.telefono     || "",
            email:             clienteCompleto.email        || "",
            estado:            clienteCompleto.estado       || "",
            fuente:            clienteCompleto.fuente       || "",
            prioridad:         clienteCompleto.prioridad    || "",
            direccion:         clienteCompleto.direccion    || "",
            notas:             clienteCompleto.notas        || "",
            proximoSeguimiento: clienteCompleto.proximoSeguimiento || "",
            responsable:       clienteCompleto.responsable  || "",
          } : null,
          cotizacionesCliente: (cotizacionesCliente || []).slice(0, 5).map(c => ({
            numeroCot: c.numeroCot || "",
            estado:    c.estado    || "",
            valor:     Number(c.valor) || 0,
            proyecto:  c.proyecto  || "",
            notas:     (c.notas    || "").slice(0, 150),
            proximoSeguimiento: c.proximoSeguimiento || "",
          })),
        }),
      });
      const data = await res.json();
      const respuesta  = data.respuesta || data.error || "Sin respuesta.";
      const intencion  = data.intencion  || "general";
      const escalacion = data.escalacion || false;
      const final = [...conMensaje, { rol: "assistant", texto: respuesta, fecha: fechaHoyLocal(), intencion, escalacion }];
      setHistorial(final);
      persistir(final);
    } catch {
      const final = [...conMensaje, { rol: "assistant", texto: "Error al conectar. Intenta de nuevo.", fecha: fechaHoyLocal() }];
      setHistorial(final);
    } finally {
      setEnviando(false);
    }
  }, [pregunta, historial, memoriaReal, enviando, tarea, nombreUsuario, persistir]);

  // Guardar feedback 👍👎 en Firestore
  const darFeedback = useCallback(async (msgIndex, valor, msg) => {
    setFeedbacks(prev => ({ ...prev, [msgIndex]: valor }));
    try {
      await addDoc(collection(db, "chatFeedback"), {
        userId,
        clienteId,
        clienteNombre:  tarea.clienteNombre || "",
        pregunta:       historial[msgIndex - 1]?.texto || "",
        respuesta:      msg.texto || "",
        intencion:      msg.intencion || "general",
        feedback:       valor,
        fecha:          fechaHoyLocal(),
        creadoEn:       serverTimestamp(),
      });
    } catch (e) {
      console.warn("Error guardando feedback:", e);
    }
  }, [userId, clienteId, tarea.clienteNombre, historial]);

  const limpiarHistorial = useCallback(() => {
    if (!window.confirm("¿Borrar todo el historial de este chat?")) return;
    setHistorial([]);
    cargadoRef.current = true;
    const ref = chatDocRef();
    if (ref) setDoc(ref, { userId, clienteId, historial: [], actualizadoEn: serverTimestamp() }).catch(() => {});
  }, [chatDocRef, userId, clienteId]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); }
  };

  const intercambios = Math.ceil(historial.length / 2);

  return (
    <div style={{ marginTop: 4 }}>
      <button
        onClick={() => setAbierto(v => !v)}
        style={{
          fontSize: 11, color: abierto ? "#3730A3" : "#3A3C7A",
          background: abierto ? "#E0E7FF" : "#EEF2FF",
          border: "none", borderRadius: 5, padding: "3px 10px",
          cursor: "pointer", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 4,
        }}
      >
        💬 {abierto ? "Cerrar chat" : `Consultar al agente${intercambios > 0 ? ` · ${intercambios}` : ""}`}
      </button>

      {abierto && (
        <div style={{ marginTop: 8, border: "1.5px solid #c7d2fe", borderRadius: 10, overflow: "hidden", background: "#fff" }}>

          {/* Header del chat con botón limpiar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", background: "#f0f4ff", borderBottom: "1px solid #e0e7ff" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#3730A3", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Agente · {tarea.clienteNombre}
            </span>
            {historial.length > 0 && (
              <button onClick={limpiarHistorial} style={{ fontSize: 10, color: "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>
                Limpiar historial
              </button>
            )}
          </div>

          {/* Historial de mensajes */}
          <div style={{ maxHeight: 240, overflowY: "auto", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8, background: "#f8fafc" }}>

            {cargando && (
              <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", fontStyle: "italic" }}>Cargando historial...</div>
            )}

            {!cargando && historial.length === 0 && (
              <div style={{ fontSize: 11, color: "#64748b", textAlign: "center", padding: "4px 0 6px" }}>
                Pregunta al agente sobre esta tarea
                {memoriaReal.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 10, color: "#94a3b8" }}>
                    El agente conoce {memoriaReal.length} interacción{memoriaReal.length > 1 ? "es" : ""} previas con este cliente
                  </div>
                )}
              </div>
            )}

            {historial.map((msg, i) => {
              const fechaActual   = msg.fecha || "";
              const fechaAnterior = i > 0 ? (historial[i - 1].fecha || "") : null;
              const mostrarFecha  = fechaActual && fechaActual !== fechaAnterior;
              const esAsistente   = msg.rol === "assistant";
              const esEscalacion  = esAsistente && msg.escalacion === true;
              const { enfoque, cuerpo } = esAsistente ? parsearRespuesta(msg.texto) : { enfoque: null, cuerpo: msg.texto };
              const intencionInfo = esAsistente && msg.intencion ? INTENCION_LABEL[msg.intencion] : null;
              const fbActual      = feedbacks[i];

              return (
                <React.Fragment key={i}>
                  {mostrarFecha && (
                    <div style={{ textAlign: "center", fontSize: 10, color: "#94a3b8", margin: "2px 0" }}>
                      {fechaActual}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: esAsistente ? "flex-start" : "flex-end", flexDirection: "column", alignItems: esAsistente ? "flex-start" : "flex-end" }}>

                    {/* Badge de intención — solo en mensajes del asistente */}
                    {intencionInfo && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 99, background: intencionInfo.bg, color: intencionInfo.color, marginBottom: 3 }}>
                        {intencionInfo.texto}
                      </span>
                    )}

                    <div style={{
                      maxWidth: "85%", padding: "7px 11px", borderRadius: 8,
                      fontSize: 12, lineHeight: 1.6,
                      background: esEscalacion ? "#FFFBEB" : esAsistente ? "#fff" : "#3A3C7A",
                      color:      esAsistente ? "#1e293b" : "#fff",
                      border:     esEscalacion ? "1.5px solid #F59E0B"
                                : esAsistente  ? "1px solid #e2e8f0" : "none",
                      borderLeft: esEscalacion ? "4px solid #F59E0B" : undefined,
                    }}>
                      {/* Razonamiento visible — línea de enfoque */}
                      {enfoque && (
                        <div style={{ fontSize: 10, color: esEscalacion ? "#92400E" : "#6366f1", fontStyle: "italic", marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${esEscalacion ? "#FDE68A" : "#e0e7ff"}` }}>
                          {esEscalacion ? "⚠️" : "🧠"} {enfoque}
                        </div>
                      )}
                      {esAsistente ? renderMensaje(cuerpo) : cuerpo}
                    </div>

                    {/* Feedback 👍👎 — solo en mensajes del asistente */}
                    {esAsistente && !fbActual && (
                      <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                        <button onClick={() => darFeedback(i, "util", msg)} style={{ fontSize: 10, background: "none", border: "1px solid #e2e8f0", borderRadius: 99, padding: "1px 7px", cursor: "pointer", color: "#64748b" }}>👍 Útil</button>
                        <button onClick={() => darFeedback(i, "no_util", msg)} style={{ fontSize: 10, background: "none", border: "1px solid #e2e8f0", borderRadius: 99, padding: "1px 7px", cursor: "pointer", color: "#64748b" }}>👎 No útil</button>
                      </div>
                    )}
                    {esAsistente && fbActual && (
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>
                        {fbActual === "util" ? "✓ Marcado como útil" : "✓ Gracias por el feedback"}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {enviando && (
              <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", paddingLeft: 4 }}>El agente está escribiendo...</span>
            )}
            <div ref={endRef} />
          </div>

          {/* Sugerencias rápidas — solo cuando no hay mensajes */}
          {!cargando && historial.length === 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, padding: "6px 10px", borderTop: "1px solid #e2e8f0", background: "#fff" }}>
              {SUGERENCIAS_CHAT.map(s => (
                <button key={s} onClick={() => enviar(s)} disabled={enviando} style={{
                  fontSize: 10, padding: "3px 8px", borderRadius: 99,
                  border: "1px solid #c7d2fe", background: "#f0f4ff", color: "#3730A3",
                  cursor: "pointer", fontWeight: 600,
                }}>
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ display: "flex", borderTop: "1px solid #e2e8f0", padding: "8px 10px", gap: 6, background: "#fff" }}>
            <textarea
              value={pregunta}
              onChange={e => setPregunta(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Escribe tu pregunta… (Enter para enviar)"
              rows={2}
              style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "inherit", resize: "none", outline: "none", color: "#1e293b" }}
            />
            <button
              onClick={() => enviar()}
              disabled={!pregunta.trim() || enviando}
              style={{
                background: pregunta.trim() && !enviando ? "#3A3C7A" : "#e2e8f0",
                color:      pregunta.trim() && !enviando ? "#fff"    : "#94a3b8",
                border: "none", borderRadius: 6, padding: "0 14px",
                fontSize: 16, fontWeight: 700, cursor: pregunta.trim() ? "pointer" : "default",
                alignSelf: "stretch", transition: "background 150ms",
              }}
            >
              ↑
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Tarjeta de tarea individual
// ═══════════════════════════════════════════════════════════════════════════════
function TareaCard({ tarea, onHecho, onReprogramar, telefono, clienteCompleto, cotizacionesCliente, esAdmin, nombreUsuario }) {
  const [guionAbierto,   setGuionAbierto]   = useState(false);
  const [completando,    setCompletando]    = useState(false);
  const [reprogramando,  setReprogramando]  = useState(false);
  const [nota,           setNota]           = useState("");
  const [dias,           setDias]           = useState(7);
  const [diasReprog,     setDiasReprog]     = useState(null);   // null = usar fecha específica
  const [fechaReprog,    setFechaReprog]    = useState("");
  const [motivoReprog,   setMotivoReprog]   = useState("");
  const [guardandoReprog, setGuardandoReprog] = useState(false);

  const p               = PRIORIDAD[tarea.prioridad] || PRIORIDAD.normal;
  const responsable     = tarea.responsable || "";
  const esClienteDeOtro = esAdmin && responsable && responsable !== nombreUsuario;
  const esManual        = tarea.id?.startsWith("manual_");

  // Lógica de permisos de reprogramación
  const esClientePropio  = !responsable || responsable === nombreUsuario;
  const puedeReprogramar = esClientePropio || esAdmin;
  const esReprogAdmin    = esAdmin && !esClientePropio; // reprogramando como admin el cliente de otro asesor

  const handleGuardar = () => onHecho(tarea, nota, dias);

  const handleConfirmarReprog = async () => {
    const nuevaFecha = diasReprog !== null ? proximoEnDias(diasReprog) : fechaReprog;
    if (!nuevaFecha) return;
    setGuardandoReprog(true);
    await onReprogramar(tarea, nuevaFecha, motivoReprog);
    setReprogramando(false);
    setDiasReprog(null);
    setFechaReprog("");
    setMotivoReprog("");
    setGuardandoReprog(false);
  };

  const handleWhatsApp = () => {
    const mensaje = tarea.guion || tarea.accion;
    if (!telefono) { alert("Este cliente no tiene número de WhatsApp registrado."); return; }
    abrirWAConGuion(telefono, mensaje);
  };

  return (
    <div className="midia-card" style={{
      background: "#fff",
      border: "0.5px solid #e2e8f0",
      borderLeft: `4px solid ${p.borde}`,
      borderRadius: 12,
      padding: "14px 16px",
      display: "flex", flexDirection: "column", gap: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      {/* ── Cabecera: badges + valor ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: p.bg, color: p.color }}>
              {p.label}
            </span>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              {TIPO_ICON[tarea.tipo] || "📌"} {tarea.tipo}
            </span>
            {esManual && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: "#f1f5f9", color: "#475569" }}>
                Manual
              </span>
            )}
            {esAdmin && responsable && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                background: esClienteDeOtro ? "#FEF3C7" : "#EEF2FF",
                color:      esClienteDeOtro ? "#92400E"  : "#3730A3",
              }}>
                {esClienteDeOtro ? "👤 " : "✓ "}{responsable}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{tarea.clienteNombre}</span>
            {tarea.numeroCot && (
              <span style={{ fontSize: 10, fontWeight: 700, background: "#EEF2FF", color: "#3A3C7A", padding: "1px 6px", borderRadius: 4 }}>
                {tarea.numeroCot}
              </span>
            )}
            {(() => {
              const fecha = clienteCompleto?.proximoSeguimiento;
              const etq   = etiquetaFechaSeguimiento(fecha);
              return etq ? (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: etq.bg, color: etq.color }}>
                  📅 {etq.texto}
                </span>
              ) : null;
            })()}
          </div>
          {tarea.clienteEmpresa && (
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{tarea.clienteEmpresa}</div>
          )}
        </div>
        {tarea.valor > 0 && (
          <div style={{ fontSize: 12, fontWeight: 700, color: "#3A3C7A", whiteSpace: "nowrap" }}>
            S/ {Number(tarea.valor).toLocaleString("es-PE")}
          </div>
        )}
      </div>

      {/* ── Acción y razón ── */}
      <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600, lineHeight: 1.4 }}>{tarea.accion}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", lineHeight: 1.5 }}>{tarea.razon}</div>

      {/* ── Guión + WhatsApp (solo fuera del modo completar) ── */}
      {!completando && tarea.guion && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setGuionAbierto(v => !v)} style={{
            fontSize: 11, color: "#3A3C7A", background: "#EEF2FF",
            border: "none", borderRadius: 5, padding: "3px 8px", cursor: "pointer", fontWeight: 600,
          }}>
            {guionAbierto ? "▲ Ocultar guión" : "▼ Ver guión"}
          </button>
          <button onClick={handleWhatsApp} style={{
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 5,
            background: "#dcfce7", color: "#15803d", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 3,
          }}>
            📱 WhatsApp
          </button>
        </div>
      )}

      {!completando && guionAbierto && tarea.guion && (
        <div style={{
          padding: "10px 12px", background: "#f8fafc", borderRadius: 8,
          fontSize: 12, color: "#475569", borderLeft: "3px solid #c7d2fe", lineHeight: 1.65,
          whiteSpace: "pre-wrap",
        }}>
          {tarea.guion}
        </div>
      )}

      {/* ── Chat con el agente ── */}
      {!completando && (
        <ChatAgente tarea={tarea} nombreUsuario={nombreUsuario} clienteCompleto={clienteCompleto} cotizacionesCliente={cotizacionesCliente} />
      )}

      {/* ── Modo completar ── */}
      {completando ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 4, borderTop: "0.5px solid #f1f5f9" }}>
          <textarea
            value={nota}
            onChange={e => setNota(e.target.value)}
            placeholder="¿Qué pasó en la llamada? (opcional)"
            rows={2}
            style={{
              width: "100%", padding: "8px 10px", border: "1.5px solid #e2e8f0",
              borderRadius: 8, fontSize: 12, fontFamily: "inherit",
              resize: "none", outline: "none", boxSizing: "border-box", color: "#1e293b",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Próximo contacto:</span>
            {[3, 7, 14, 30].map(d => (
              <button key={d} onClick={() => setDias(d)} style={{
                fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "none",
                cursor: "pointer", fontWeight: 700,
                background: dias === d ? "#F97316" : "#f1f5f9",
                color:      dias === d ? "#000"     : "#475569",
                transition: "background 150ms ease-out",
              }}>
                {d}d
              </button>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={() => setCompletando(false)} style={{
              background: "#f1f5f9", color: "#475569", border: "none",
              borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer",
            }}>
              Cancelar
            </button>
            <button onClick={handleGuardar} style={{
              background: "#dcfce7", color: "#15803d", border: "none",
              borderRadius: 6, padding: "6px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
              ✓ Guardar
            </button>
          </div>
        </div>
      ) : reprogramando ? (
        /* ── Formulario de reprogramación ── */
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 8, borderTop: "0.5px solid #f1f5f9" }}>
          {/* Aviso si es admin reprogramando cliente de otro asesor */}
          {esReprogAdmin && (
            <div style={{ background: "#FEF3C7", borderRadius: 7, padding: "7px 10px", fontSize: 11, color: "#92400E", display: "flex", gap: 5 }}>
              <span>⚠️</span>
              <span>Estás reprogramando como <strong>administrador</strong> el cliente de <strong>{responsable}</strong>. Esta acción quedará registrada.</span>
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>Nueva fecha de seguimiento:</div>

          {/* Opciones rápidas de días */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {[1, 3, 7, 14, 30].map(d => (
              <button key={d} onClick={() => { setDiasReprog(d); setFechaReprog(""); }} style={{
                fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "none",
                cursor: "pointer", fontWeight: 700,
                background: diasReprog === d ? "#F97316" : "#f1f5f9",
                color:      diasReprog === d ? "#000"    : "#475569",
                transition: "background 150ms",
              }}>
                {d}d
              </button>
            ))}
          </div>

          {/* Fecha específica */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: "#64748b" }}>o fecha exacta:</span>
            <input
              type="date"
              value={fechaReprog}
              min={fechaHoyLocal()}
              onChange={e => { setFechaReprog(e.target.value); setDiasReprog(null); }}
              style={{ padding: "4px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, outline: "none", fontFamily: "inherit" }}
            />
          </div>

          {/* Motivo */}
          <input
            type="text"
            value={motivoReprog}
            onChange={e => setMotivoReprog(e.target.value)}
            placeholder="Motivo (opcional): viaje, obra pausada, etc."
            style={{ width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12, outline: "none", boxSizing: "border-box", fontFamily: "inherit", color: "#1e293b" }}
          />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={() => { setReprogramando(false); setDiasReprog(null); setFechaReprog(""); setMotivoReprog(""); }} style={{
              background: "#f1f5f9", color: "#475569", border: "none",
              borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer",
            }}>
              Cancelar
            </button>
            <button
              onClick={handleConfirmarReprog}
              disabled={guardandoReprog || (diasReprog === null && !fechaReprog)}
              style={{
                background: guardandoReprog || (diasReprog === null && !fechaReprog) ? "#e2e8f0" : (esReprogAdmin ? "#F97316" : "#3A3C7A"),
                color:  guardandoReprog || (diasReprog === null && !fechaReprog) ? "#94a3b8" : "#fff",
                border: "none", borderRadius: 6, padding: "6px 16px",
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}
            >
              {guardandoReprog ? "Guardando..." : "↷ Confirmar"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 4, borderTop: "0.5px solid #f1f5f9", marginTop: 2 }}>
          {tarea.clienteId ? (
            <Link to={`/clientes/${tarea.clienteId}/perfil`} style={{ fontSize: 11, color: "#3A3C7A", fontWeight: 600, textDecoration: "none", padding: "4px 0", display: "flex", alignItems: "center", gap: 3 }}>
              <span style={{ fontSize: 12 }}>↗</span> Perfil 360°
            </Link>
          ) : <span />}
          <div style={{ display: "flex", gap: 6 }}>
            {puedeReprogramar && (
              <button onClick={() => setReprogramando(true)} className="midia-btn" style={{
                background: esReprogAdmin ? "#FEF3C7" : "#f8fafc",
                color:      esReprogAdmin ? "#92400E"  : "#64748b",
                border: `1px solid ${esReprogAdmin ? "#FCD34D" : "#e2e8f0"}`,
                borderRadius: 8, padding: "6px 12px",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}>
                ↷ {esReprogAdmin ? "Reprogramar (Admin)" : "Reprogramar"}
              </button>
            )}
            <button onClick={() => setCompletando(true)} className="midia-btn-primary" style={{
              background: "linear-gradient(135deg, #22c55e, #16a34a)", color: "#fff", border: "none",
              borderRadius: 8, padding: "6px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 2px 8px rgba(34,197,94,0.30)",
            }}>
              ✓ Hecho
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Agregar tarea manual
// ═══════════════════════════════════════════════════════════════════════════════
const TIPOS_MANUAL = ["seguimiento", "llamada", "cotizacion", "visita"];
const INICIAL_FORM = { clienteId: "", tipo: "seguimiento", accion: "" };

function AgregarTareaManual({ clientes, nombreUsuario, onAgregar }) {
  const [abierto, setAbierto] = useState(false);
  const [form,    setForm]    = useState(INICIAL_FORM);
  const [busq,    setBusq]    = useState("");

  const clientesFiltrados = useMemo(() => {
    if (!busq) return clientes.slice(0, 40);
    const b = busq.toLowerCase();
    return clientes.filter(c =>
      c.nombre?.toLowerCase().includes(b) || c.empresa?.toLowerCase().includes(b)
    ).slice(0, 40);
  }, [clientes, busq]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.accion.trim()) return;
    const cliente = clientes.find(c => c.id === form.clienteId) || {};
    onAgregar({
      id:             `manual_${Date.now()}`,
      prioridad:      "normal",
      tipo:           form.tipo,
      clienteId:      form.clienteId || "",
      clienteNombre:  cliente.nombre  || "Sin cliente",
      clienteEmpresa: cliente.empresa || "",
      proyecto:       cliente.proyecto || "",
      notas:          cliente.notas    || "",
      accion:         form.accion.trim(),
      razon:          "Tarea agregada manualmente",
      guion:          "",
      valor:          Number(cliente.valor) || 0,
      responsable:    cliente.responsable || nombreUsuario,
    });
    setForm(INICIAL_FORM);
    setBusq("");
    setAbierto(false);
  };

  return (
    <div>
      <button
        onClick={() => setAbierto(v => !v)}
        style={{
          background: abierto ? "#f1f5f9" : "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 8,
          padding: "7px 14px", fontSize: 12, fontWeight: 600,
          color: "#475569", cursor: "pointer",
        }}
      >
        {abierto ? "✕ Cancelar" : "+ Agregar tarea"}
      </button>

      {abierto && (
        <form onSubmit={handleSubmit} style={{
          marginTop: 10, background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: 12, padding: "18px 20px",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#3A3C7A", margin: 0, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Nueva tarea manual
          </p>

          {/* Buscar cliente */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
              Buscar cliente
            </label>
            <input
              type="text"
              placeholder="Nombre o empresa..."
              value={busq}
              onChange={e => { setBusq(e.target.value); setForm(f => ({ ...f, clienteId: "" })); }}
              style={{
                width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0",
                borderRadius: 7, fontSize: 12, outline: "none", boxSizing: "border-box", fontFamily: "inherit",
              }}
            />
            {busq && (
              <div style={{
                border: "1px solid #e2e8f0", borderRadius: 7, marginTop: 4,
                maxHeight: 160, overflowY: "auto", background: "#fff",
              }}>
                {clientesFiltrados.length === 0 ? (
                  <div style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8" }}>Sin resultados</div>
                ) : clientesFiltrados.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, clienteId: c.id })); setBusq(`${c.nombre} — ${c.empresa || ""}`.trim().replace(/—\s*$/, "")); }}
                    style={{
                      width: "100%", textAlign: "left", padding: "8px 12px",
                      border: "none", background: form.clienteId === c.id ? "#EEF2FF" : "#fff",
                      cursor: "pointer", fontSize: 12, color: "#1e293b",
                      borderBottom: "0.5px solid #f1f5f9",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{c.nombre}</span>
                    {c.empresa && <span style={{ color: "#94a3b8" }}> — {c.empresa}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tipo */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Tipo</label>
            <div style={{ display: "flex", gap: 6 }}>
              {TIPOS_MANUAL.map(t => (
                <button
                  key={t} type="button"
                  onClick={() => setForm(f => ({ ...f, tipo: t }))}
                  style={{
                    fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "none",
                    cursor: "pointer", fontWeight: 600,
                    background: form.tipo === t ? "#3A3C7A" : "#f1f5f9",
                    color:      form.tipo === t ? "#fff"    : "#475569",
                  }}
                >
                  {TIPO_ICON[t]} {t}
                </button>
              ))}
            </div>
          </div>

          {/* Acción */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
              ¿Qué tienes que hacer? *
            </label>
            <input
              type="text"
              value={form.accion}
              onChange={e => setForm(f => ({ ...f, accion: e.target.value }))}
              placeholder="Ej: Llamar para confirmar cotización de fierro"
              required
              style={{
                width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0",
                borderRadius: 7, fontSize: 12, outline: "none",
                boxSizing: "border-box", fontFamily: "inherit", color: "#1e293b",
              }}
            />
          </div>

          <button type="submit" style={{
            background: "#1e3a5f", color: "#fff", border: "none",
            borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
            Agregar tarea
          </button>
        </form>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Cierre del día
// ═══════════════════════════════════════════════════════════════════════════════
function CierreDia({ tareasHechas, tareasOmitidas, nombreUsuario, onReintentar }) {
  const [resumen,  setResumen]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const todoCompleto = tareasOmitidas.length === 0 && tareasHechas.length > 0;

  useEffect(() => {
    // Auto-generar resumen si se completó todo
    if (todoCompleto && !resumen && !loading) generarResumen();
  }, [todoCompleto]); // eslint-disable-line react-hooks/exhaustive-deps

  const generarResumen = async () => {
    setLoading(true);
    setError(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/cierre-dia", {
        method:  "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          tareasCompletadas: tareasHechas,
          tareasOmitidas,
          nombreUsuario,
          fechaHoy: fechaHoyLocal(),
        }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResumen(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: todoCompleto ? "linear-gradient(135deg, #f0fdf4, #dcfce7)" : "#fff",
      border: `1px solid ${todoCompleto ? "#86efac" : "#e2e8f0"}`,
      borderRadius: 14, overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 38, marginBottom: 8 }}>{todoCompleto ? "🎉" : "📊"}</div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: todoCompleto ? "#15803d" : "#1e293b" }}>
          {todoCompleto ? "¡Jornada completada!" : "Cierre del día"}
        </h3>
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "#64748b" }}>
          {tareasHechas.length} completadas · {tareasOmitidas.length} pendientes
        </p>
      </div>

      {/* Resumen generado */}
      {loading && (
        <div style={{ padding: "8px 24px 20px", textAlign: "center" }}>
          <div className="dots-loader" style={{ paddingTop: 0, paddingBottom: 8 }}>
            <span /><span /><span />
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>El agente está generando tu resumen...</div>
        </div>
      )}

      {error && (
        <div style={{ padding: "0 24px 16px" }}>
          <div style={{ background: "#FEE2E2", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#DC2626" }}>
            {error}{" "}
            <button onClick={generarResumen} style={{ background: "none", border: "none", color: "#DC2626", textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}>
              Reintentar
            </button>
          </div>
        </div>
      )}

      {resumen && !loading && (
        <div style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Felicitación */}
          <div style={{ background: "rgba(255,255,255,0.7)", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#1e293b", lineHeight: 1.6, fontStyle: "italic", borderLeft: "3px solid #22c55e" }}>
            "{resumen.felicitacion}"
          </div>

          {/* Logros */}
          {resumen.logros?.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 0 3px rgba(34,197,94,0.15)", flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.07em" }}>Logros del día</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: "#dcfce7", color: "#15803d" }}>{resumen.logros.length}</span>
                <div style={{ flex: 1, height: "0.5px", background: "#bbf7d0" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {resumen.logros.map((l, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, fontSize: 12, color: "#1e293b" }}>
                    <span style={{ color: "#22c55e", flexShrink: 0 }}>•</span>
                    <span>{l}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pendientes */}
          {resumen.pendientes?.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#D97706", boxShadow: "0 0 0 3px rgba(217,119,6,0.15)", flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#B45309", textTransform: "uppercase", letterSpacing: "0.07em" }}>Pendientes</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: "#fef3c7", color: "#B45309" }}>{resumen.pendientes.length}</span>
                <div style={{ flex: 1, height: "0.5px", background: "#fcd34d" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {resumen.pendientes.map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, fontSize: 12, color: "#1e293b" }}>
                    <span style={{ color: "#F59E0B", flexShrink: 0 }}>•</span>
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prioridades de mañana */}
          {resumen.manana?.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#3A3C7A", boxShadow: "0 0 0 3px rgba(58,60,122,0.15)", flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#3A3C7A", textTransform: "uppercase", letterSpacing: "0.07em" }}>Prioridades para mañana</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: "#eef2ff", color: "#3A3C7A" }}>{resumen.manana.length}</span>
                <div style={{ flex: 1, height: "0.5px", background: "#c7d2fe" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {resumen.manana.map((m, i) => (
                  <div key={i} style={{
                    display: "flex", gap: 10, padding: "8px 12px",
                    background: "rgba(255,255,255,0.8)", borderRadius: 8,
                    fontSize: 12, color: "#1e293b", alignItems: "flex-start",
                  }}>
                    <span style={{ fontWeight: 700, color: "#3A3C7A", flexShrink: 0, marginTop: 1 }}>{i + 1}.</span>
                    <span>{m}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={onReintentar} style={{
            background: "rgba(255,255,255,0.8)", border: "1px solid #e2e8f0",
            borderRadius: 8, padding: "8px 0", fontSize: 12, color: "#475569",
            cursor: "pointer", fontWeight: 600, marginTop: 4,
          }}>
            ↺ Regenerar resumen
          </button>
        </div>
      )}

      {/* Botón generar (solo si aún no se generó y no está cargando) */}
      {!resumen && !loading && !error && (
        <div style={{ padding: "0 24px 20px", textAlign: "center" }}>
          <button onClick={generarResumen} style={{
            background: "linear-gradient(135deg, #1E3A5F, #3A3C7A)",
            color: "#fff", border: "none", borderRadius: 10,
            padding: "11px 32px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(30,58,95,0.30)",
          }}>
            📊 Generar resumen del día
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Sección de tareas (urgente / importante / normal)
// ═══════════════════════════════════════════════════════════════════════════════
function Section({ titulo, items, onHecho, onReprogramar, telefonosMap, clientesMap, cotizacionesPorCliente, esAdmin, nombreUsuario }) {
  const esUrgente   = titulo.includes("Urgente");
  const esImportante = titulo.includes("Importante");
  const dotColor = esUrgente ? "#DC2626" : esImportante ? "#D97706" : "#94a3b8";
  const chipBg   = esUrgente ? "#FEE2E2" : esImportante ? "#FEF3C7" : "#f1f5f9";
  const chipCol  = esUrgente ? "#DC2626" : esImportante ? "#B45309" : "#64748b";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 2px" }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0,
          boxShadow: esUrgente ? "0 0 0 3px rgba(220,38,38,0.15)" : esImportante ? "0 0 0 3px rgba(217,119,6,0.15)" : "none",
        }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.07em" }}>
          {titulo.replace(/^[^\w]+/, "").replace(/\s+\(\d+\)$/, "")}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: chipBg, color: chipCol }}>
          {items.length}
        </span>
        <div style={{ flex: 1, height: "0.5px", background: "#e2e8f0" }} />
      </div>
      {items.map((t, i) => (
        <div key={t.id} className="stagger-item" style={{ animationDelay: `${i * 50}ms` }}>
          <TareaCard
            tarea={t}
            onHecho={onHecho}
            onReprogramar={onReprogramar}
            telefono={telefonosMap[t.clienteId]}
            clienteCompleto={clientesMap?.[t.clienteId] || null}
            cotizacionesCliente={cotizacionesPorCliente?.[t.clienteId] || []}
            esAdmin={esAdmin}
            nombreUsuario={nombreUsuario}
          />
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function MiDia() {
  const { user }            = useAuth();
  const { perfil, esAdmin } = useRol();
  const { clientes,     loading: lC   }                        = useClientes();
  const { cotizaciones, loading: lCot, actualizarCotizacion }  = useCotizaciones();
  const { pedidos }     = usePedidos();
  const { reuniones }   = useReuniones();
  const recompraData    = useRecompra(clientes, pedidos, reuniones);

  // ── Clientes dormidos: tuvieron al menos 1 Ganada pero llevan 45d+ sin actividad
  // ni seguimiento agendado. Se muestran en Mi Día para reactivarlos.
  const clientesDormidos = useMemo(() => {
    const DIAS = 45;
    const ahora = Date.now();
    const GANADAS = ["Ganada", "Cerrada", "Aprobada"];
    return clientes.filter(cli => {
      if (cli.proximoSeguimiento) return false;
      const cotsCli = cotizaciones.filter(c => c.clienteId === cli.id);
      if (!cotsCli.some(c => GANADAS.includes(c.estado))) return false;
      if (cotsCli.some(c => c.estado === "Pendiente")) return false;
      const ultima = cotsCli
        .map(c => { const d = c.creadoEn?.toDate ? c.creadoEn.toDate() : new Date(c.creadoEn || 0); return d.getTime(); })
        .filter(t => t > 0).sort((a, b) => b - a)[0];
      if (!ultima) return false;
      return Math.round((ahora - ultima) / 86400000) > DIAS;
    }).slice(0, 5);
  }, [clientes, cotizaciones]);

  const {
    tareas, todasLasTareas, totalTareas, resumen,
    loading, error, completadas, notasCompletadas,
    marcarHecho, marcarOmitida, recargar,
  } = useMiDia(clientes, cotizaciones, perfil, user?.uid);

  // ── Tareas manuales (persistidas en Firestore — antes se perdían al refrescar) ──
  const { tareasManual, agregarTareaManual, completarTareaManual } = useTareasManuales();

  // ── Vista admin: "todas" | "mias" | "equipo" ─────────────────────────────
  const [vistaAdmin, setVistaAdmin] = useState("todas");

  // ── Cierre del día ────────────────────────────────────────────────────────
  const [mostrarCierre, setMostrarCierre] = useState(false);

  // ── Mapa telefono por clienteId ───────────────────────────────────────────
  const telefonosMap = useMemo(() => {
    const m = {};
    clientes.forEach(c => { if (c.telefono) m[c.id] = c.telefono; });
    return m;
  }, [clientes]);

  // ── Mapa perfil completo por clienteId (para el chat del agente) ──────────
  const clientesMap = useMemo(() => {
    const m = {};
    clientes.forEach(c => { m[c.id] = c; });
    return m;
  }, [clientes]);

  // ── Mapa cotizaciones por clienteId (para el chat del agente) ────────────
  const cotizacionesPorCliente = useMemo(() => {
    const m = {};
    cotizaciones.forEach(c => {
      if (!c.clienteId) return;
      if (!m[c.clienteId]) m[c.clienteId] = [];
      m[c.clienteId].push(c);
    });
    return m;
  }, [cotizaciones]);

  // ── Totales combinados (AI + manual) ──────────────────────────────────────
  const tareasManualPendientes = tareasManual.filter(t => !t.completada);
  const totalConManual         = totalTareas + tareasManual.length;
  const totalCompletadas       = completadas.length + tareasManual.filter(t => t.completada).length;

  // ── Filtro por vista admin ────────────────────────────────────────────────
  const tareasFiltradas = useMemo(() => {
    const todas = [...tareas, ...tareasManualPendientes];
    if (!esAdmin || vistaAdmin === "todas") return todas;
    if (vistaAdmin === "mias")
      return todas.filter(t => t.id?.startsWith("manual_") || !t.responsable || t.responsable === perfil?.nombre);
    if (vistaAdmin === "equipo")
      return todas.filter(t => !t.id?.startsWith("manual_") && t.responsable && t.responsable !== perfil?.nombre);
    return todas;
  }, [tareas, tareasManualPendientes, esAdmin, vistaAdmin, perfil]);

  // Conteos para los badges de cada tab
  const cntMias   = useMemo(() => [...tareas, ...tareasManualPendientes].filter(t =>
    t.id?.startsWith("manual_") || !t.responsable || t.responsable === perfil?.nombre).length,
  [tareas, tareasManualPendientes, perfil]);
  const cntEquipo = useMemo(() => [...tareas, ...tareasManualPendientes].filter(t =>
    !t.id?.startsWith("manual_") && t.responsable && t.responsable !== perfil?.nombre).length,
  [tareas, tareasManualPendientes, perfil]);

  const todosPendientes = tareasFiltradas;
  const pct = totalConManual > 0 ? Math.round((totalCompletadas / totalConManual) * 100) : 0;

  // Auto-abrir cierre cuando se completa todo
  useEffect(() => {
    if (totalConManual > 0 && todosPendientes.length === 0 && !mostrarCierre) {
      setMostrarCierre(true);
    }
  }, [todosPendientes.length, totalConManual]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Datos para cierre del día ─────────────────────────────────────────────
  const tareasHechasParaCierre = useMemo(() => [
    ...todasLasTareas.filter(t => completadas.includes(t.id)).map(t => ({ ...t, nota: notasCompletadas[t.id] || "" })),
    ...tareasManual.filter(t => t.completada).map(t => ({ ...t, nota: t.notaCompletado || "" })),
  ], [todasLasTareas, completadas, notasCompletadas, tareasManual]);

  const tareasOmitidasParaCierre = useMemo(() => [
    ...todasLasTareas.filter(t => !completadas.includes(t.id)),
    ...tareasManual.filter(t => !t.completada),
  ], [todasLasTareas, completadas, tareasManual]);

  // ── Marcar hecho (AI y manual unificado) ─────────────────────────────────
  const handleHecho = useCallback(async (tarea, nota, dias) => {
    const esManual = tarea.id?.startsWith("manual_");

    // Marcar como completada en el estado correcto
    if (esManual) {
      await completarTareaManual(tarea.id, nota);
    } else {
      marcarHecho(tarea.id, nota);
    }

    const proximoContacto = proximoEnDias(dias);
    const fechaHoy        = fechaHoyLocal();
    const tipoActividad   = TIPO_MAP[tarea.tipo] || "Llamada";

    // Guardar actividad en el perfil del cliente
    if (tarea.clienteId) {
      await crearActividadCliente({
        clienteId:      tarea.clienteId,
        tipo:           tipoActividad,
        descripcion:    nota || tarea.accion,
        fecha:          fechaHoy,
        proximoContacto,
        fuente:         "agente",
      });
    }

    // Actualizar proximoSeguimiento de la cotización si aplica
    if (tarea.cotizacionId) {
      await actualizarCotizacion(tarea.cotizacionId, {
        proximoSeguimiento: proximoContacto,
        ultimoContacto:     fechaHoy,
      });
    }

    // Guardar en memoria del agente para personalizar planes futuros
    await guardarInteraccion({
      userId:        user.uid,
      clienteId:     tarea.clienteId,
      clienteNombre: tarea.clienteNombre,
      tipo:          tarea.tipo,
      accion:        tarea.accion,
      nota,
      dias,
    });
  }, [marcarHecho, actualizarCotizacion, user, completarTareaManual]);

  // ── Reprogramar seguimiento ───────────────────────────────────────────────
  // Actualiza: cliente.proximoSeguimiento + cotizaciones abiertas + actividad log
  // Permisos: asesor solo sus clientes | admin puede reprogramar cualquiera
  const handleReprogramar = useCallback(async (tarea, nuevaFecha, motivo) => {
    const esManual     = tarea.id?.startsWith("manual_");
    const esComoAdmin  = esAdmin && tarea.responsable && tarea.responsable !== perfil?.nombre;
    const fechaHoy     = fechaHoyLocal();

    if (!esManual && tarea.clienteId) {
      // 1. Actualizar cliente.proximoSeguimiento (fuente única de verdad)
      await updateDoc(doc(db, "clientes", tarea.clienteId), {
        proximoSeguimiento: nuevaFecha,
        actualizadoEn:      serverTimestamp(),
      }).catch(e => console.error("Error actualizando cliente:", e));

      // 2. Actualizar todas las cotizaciones abiertas del cliente
      try {
        const cotSnap = await getDocs(
          query(collection(db, "cotizaciones"),
            where("clienteId", "==", tarea.clienteId),
          )
        );
        const estadosCerrados = ["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"];
        const updates = cotSnap.docs
          .filter(d => !estadosCerrados.includes(d.data().estado))
          .map(d => updateDoc(doc(db, "cotizaciones", d.id), {
            proximoSeguimiento: nuevaFecha,
            actualizadoEn:      serverTimestamp(),
          }));
        await Promise.all(updates);
      } catch (e) { console.warn("No se pudo actualizar cotizaciones:", e.message); }

      // 3. Registrar en historial de actividades del cliente
      await crearActividadCliente({
        clienteId:      tarea.clienteId,
        tipo:           "Reprogramación",
        descripcion:    motivo
          ? `Reprogramado al ${nuevaFecha}: ${motivo}${esComoAdmin ? " (por administrador)" : ""}`
          : `Seguimiento reprogramado al ${nuevaFecha}${esComoAdmin ? " (por administrador)" : ""}`,
        fecha:          fechaHoy,
        proximoContacto: nuevaFecha,
        fuente:          esComoAdmin ? "admin" : "asesor",
      });
    }

    // 4. Ocultar la tarea del plan de hoy (sin marcarla como "hecha" con nota)
    if (esManual) {
      await completarTareaManual(tarea.id, "");
    } else {
      marcarOmitida(tarea.id);
    }
  }, [esAdmin, perfil, marcarOmitida, completarTareaManual]);

  // ── Agregar tarea manual ──────────────────────────────────────────────────
  const handleAgregarTarea = useCallback((tarea) => {
    agregarTareaManual(tarea);
  }, [agregarTareaManual]);

  // ── Filtros por prioridad (sobre la vista activa) ─────────────────────────
  const urgentes    = tareasFiltradas.filter(t => !t.id?.startsWith("manual_") && t.prioridad === "urgente");
  const importantes = tareasFiltradas.filter(t => !t.id?.startsWith("manual_") && t.prioridad === "importante");
  const normales    = tareasFiltradas.filter(t => !t.id?.startsWith("manual_") && t.prioridad === "normal");
  const manualesFiltrados = tareasFiltradas.filter(t => t.id?.startsWith("manual_"));
  const cargandoDatos = lC || lCot;
  const hoy = new Date().toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long" });

  const horaActual    = new Date().getHours();
  const saludo        = horaActual < 12 ? "Buenos días" : horaActual < 18 ? "Buenas tardes" : "Buenas noches";
  const nombreCorto   = (perfil?.nombre || "Asesor").split(" ")[0];
  const urgentesCount = tareasFiltradas?.filter(t => (PRIORIDAD[t.prioridad] || PRIORIDAD.normal).label === "URGENTE").length || 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 700 }}>
      <style>{`
        .midia-card {
          transition: box-shadow 200ms cubic-bezier(0.23,1,0.32,1),
                      transform  200ms cubic-bezier(0.23,1,0.32,1);
        }
        .midia-card:hover {
          box-shadow: 0 4px 20px rgba(30,58,95,0.10);
          transform: translateY(-1px);
        }
        .midia-btn {
          transition: background 150ms cubic-bezier(0.23,1,0.32,1),
                      transform  100ms cubic-bezier(0.23,1,0.32,1);
        }
        .midia-btn:hover  { filter: brightness(0.95); }
        .midia-btn:active { transform: scale(0.95) !important; }
        .midia-btn-primary {
          transition: box-shadow 150ms cubic-bezier(0.23,1,0.32,1),
                      transform  100ms cubic-bezier(0.23,1,0.32,1);
        }
        .midia-btn-primary:hover  { filter: brightness(1.06); }
        .midia-btn-primary:active { transform: scale(0.95) !important; }
        .midia-header-btn {
          transition: background 150ms cubic-bezier(0.23,1,0.32,1),
                      transform  100ms cubic-bezier(0.23,1,0.32,1),
                      box-shadow 150ms cubic-bezier(0.23,1,0.32,1);
        }
        .midia-header-btn:hover  { filter: brightness(0.96); }
        .midia-header-btn:active { transform: scale(0.96) !important; }
        @keyframes midia-pulse {
          0%,100% { opacity: 1; }
          50%      { opacity: 0.4; }
        }
        @keyframes midia-bar-in {
          from { width: 0%; }
        }
        @keyframes midia-bounce-in {
          0%   { opacity: 0; transform: scale(0.85) translateY(8px); }
          60%  { transform: scale(1.03) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        background: "linear-gradient(135deg, #1E3A5F 0%, #3A3C7A 100%)",
        borderRadius: 16, padding: "18px 22px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
        boxShadow: "0 4px 20px rgba(30,58,95,0.18)",
      }}>
        <div>
          <div style={{ fontSize: 12, color: "#c7d2fe", fontWeight: 500, marginBottom: 2, textTransform: "capitalize" }}>
            {hoy}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.3px" }}>
            {saludo}, {nombreCorto}
          </h1>
          {!loading && !cargandoDatos && totalConManual > 0 && (
            <div style={{ fontSize: 12, color: "#a5b4fc", marginTop: 4 }}>
              {urgentesCount > 0
                ? `⚡ ${urgentesCount} urgente${urgentesCount > 1 ? "s" : ""} · ${totalConManual} acciones en total`
                : `${totalConManual} acciones para mover el negocio hoy`}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {totalConManual > 0 && !loading && !cargandoDatos && (
            <button
              onClick={() => setMostrarCierre(v => !v)}
              className="midia-header-btn"
              style={{
                background: mostrarCierre ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.12)",
                border: `1.5px solid ${mostrarCierre ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.2)"}`,
                borderRadius: 10, padding: "7px 14px", fontSize: 12, fontWeight: 600,
                color: mostrarCierre ? "#4ade80" : "#e2e8f0", cursor: "pointer",
              }}
            >
              {mostrarCierre ? "← Plan del día" : "📊 Cerrar día"}
            </button>
          )}
          <button
            onClick={recargar}
            disabled={loading || cargandoDatos}
            className="midia-header-btn"
            style={{
              background: "rgba(255,255,255,0.12)", border: "1.5px solid rgba(255,255,255,0.2)",
              borderRadius: 10, padding: "7px 14px", fontSize: 12, fontWeight: 600,
              color: "#e2e8f0", cursor: "pointer", opacity: loading ? 0.5 : 1,
            }}
          >
            {loading ? "Analizando..." : "↺ Regenerar"}
          </button>
        </div>
      </div>

      {/* ── Estados de carga ── */}
      {cargandoDatos && (
        <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 14, padding: "28px 24px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 12 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: "50%", background: "#3A3C7A",
                animation: `midia-pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>Cargando tus datos...</div>
        </div>
      )}

      {!cargandoDatos && loading && (
        <div style={{
          background: "linear-gradient(135deg, #EEF2FF, #F5F3FF)",
          border: "1px solid #c7d2fe", borderRadius: 14, padding: "28px 24px", textAlign: "center",
          animation: "midia-bounce-in 400ms cubic-bezier(0.23,1,0.32,1) forwards",
        }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🤖</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#3A3C7A", marginBottom: 6 }}>
            Analizando tu cartera...
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 5, marginBottom: 8 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: "50%", background: "#7C3AED",
                animation: `midia-pulse 1s ease-in-out ${i * 0.25}s infinite`,
              }} />
            ))}
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            {clientes.length} clientes · {cotizaciones.length} cotizaciones
          </div>
        </div>
      )}

      {!loading && error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "14px 18px", fontSize: 13, color: "#DC2626", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div style={{ flex: 1 }}>Error al generar el plan: <strong>{error}</strong></div>
          <button onClick={recargar} className="midia-btn" style={{
            background: "#fff", border: "1px solid #FECACA", color: "#DC2626",
            borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>
            Reintentar
          </button>
        </div>
      )}

      {/* ── Vista principal: cierre O plan ── */}
      {!cargandoDatos && !loading && !error && (
        <>
          {mostrarCierre ? (
            /* ── CIERRE DEL DÍA ── */
            <CierreDia
              tareasHechas={tareasHechasParaCierre}
              tareasOmitidas={tareasOmitidasParaCierre}
              nombreUsuario={perfil?.nombre || "Asesor"}
              onReintentar={() => {}}
            />
          ) : (
            /* ── PLAN DEL DÍA ── */
            totalConManual > 0 ? (
              <>
                {/* Resumen del agente + barra de progreso */}
                <div style={{
                  background: "#fff", border: "0.5px solid #e2e8f0",
                  borderRadius: 14, padding: "16px 20px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}>
                  {resumen && (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                        background: "linear-gradient(135deg, #3A3C7A, #7C3AED)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 18, boxShadow: "0 2px 8px rgba(124,58,237,0.25)",
                      }}>🤖</div>
                      <div style={{
                        flex: 1, background: "#f8fafc", borderRadius: "4px 12px 12px 12px",
                        padding: "10px 14px", fontSize: 13, color: "#475569", lineHeight: 1.65,
                        border: "0.5px solid #e2e8f0",
                      }}>
                        {resumen}
                      </div>
                    </div>
                  )}
                  {/* Barra de progreso */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, height: 10, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                      <div style={{
                        height: "100%",
                        width: `${pct}%`,
                        background: pct === 100
                          ? "linear-gradient(90deg, #22c55e, #16a34a)"
                          : "linear-gradient(90deg, #3A3C7A, #7C3AED)",
                        borderRadius: 99,
                        transition: "width 700ms cubic-bezier(0.23,1,0.32,1)",
                        boxShadow: pct > 0 ? (pct === 100 ? "0 0 8px rgba(34,197,94,0.4)" : "0 0 8px rgba(124,58,237,0.3)") : "none",
                      }} />
                    </div>
                    <div style={{
                      fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                      color: pct === 100 ? "#16a34a" : "#3A3C7A",
                      minWidth: 70, textAlign: "right",
                    }}>
                      {totalCompletadas}/{totalConManual}
                      <span style={{ fontSize: 10, fontWeight: 400, color: "#94a3b8", marginLeft: 3 }}>
                        {pct}%
                      </span>
                    </div>
                  </div>
                  {pct === 100 && (
                    <div style={{ textAlign: "center", marginTop: 10, fontSize: 12, fontWeight: 600, color: "#16a34a" }}>
                      🎉 ¡Todo completado!
                    </div>
                  )}
                </div>

                {/* ── Tabs admin: Todas / Mis tareas / Equipo ── */}
                {esAdmin && (cntMias > 0 || cntEquipo > 0) && (
                  <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3, gap: 2 }}>
                    {[
                      { key: "todas",  label: `Todas`,         count: totalConManual - totalCompletadas },
                      { key: "mias",   label: `Mis tareas`,    count: cntMias },
                      { key: "equipo", label: `Equipo`,        count: cntEquipo },
                    ].map(({ key, label, count }) => (
                      <button
                        key={key}
                        onClick={() => setVistaAdmin(key)}
                        style={{
                          flex: 1, padding: "7px 4px", borderRadius: 8, border: "none",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                          background: vistaAdmin === key ? "#fff" : "transparent",
                          color:      vistaAdmin === key ? "#1e293b" : "#64748b",
                          boxShadow:  vistaAdmin === key ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                          transition: "all 150ms",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                        }}
                      >
                        {label}
                        {count > 0 && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 99,
                            background: vistaAdmin === key
                              ? (key === "equipo" ? "#FEF3C7" : "#EEF2FF")
                              : "#e2e8f0",
                            color: vistaAdmin === key
                              ? (key === "equipo" ? "#92400E" : "#3730A3")
                              : "#94a3b8",
                          }}>
                            {count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Botón + formulario de tarea manual */}
                <AgregarTareaManual
                  clientes={clientes}
                  nombreUsuario={perfil?.nombre || "Asesor"}
                  onAgregar={handleAgregarTarea}
                />

                {/* Tareas por prioridad */}
                {todosPendientes.length > 0 ? (
                  <>
                    {urgentes.length > 0 && (
                      <Section titulo="🔴 Urgente" items={urgentes} onHecho={handleHecho} onReprogramar={handleReprogramar} telefonosMap={telefonosMap} clientesMap={clientesMap} cotizacionesPorCliente={cotizacionesPorCliente} esAdmin={esAdmin} nombreUsuario={perfil?.nombre} />
                    )}
                    {importantes.length > 0 && (
                      <Section titulo="🟡 Importante" items={importantes} onHecho={handleHecho} onReprogramar={handleReprogramar} telefonosMap={telefonosMap} clientesMap={clientesMap} cotizacionesPorCliente={cotizacionesPorCliente} esAdmin={esAdmin} nombreUsuario={perfil?.nombre} />
                    )}
                    {normales.length > 0 && (
                      <Section titulo="⚪ Normal" items={normales} onHecho={handleHecho} onReprogramar={handleReprogramar} telefonosMap={telefonosMap} clientesMap={clientesMap} cotizacionesPorCliente={cotizacionesPorCliente} esAdmin={esAdmin} nombreUsuario={perfil?.nombre} />
                    )}
                    {manualesFiltrados.length > 0 && (
                      <Section titulo="✏️ Manuales" items={manualesFiltrados} onHecho={handleHecho} onReprogramar={handleReprogramar} telefonosMap={telefonosMap} clientesMap={clientesMap} cotizacionesPorCliente={cotizacionesPorCliente} esAdmin={esAdmin} nombreUsuario={perfil?.nombre} />
                    )}
                  </>
                ) : (
                  /* Todas las tareas completadas */
                  <div style={{
                    background: "linear-gradient(135deg, #F0FDF4, #DCFCE7)",
                    border: "1px solid #86EFAC", borderRadius: 16,
                    padding: "32px 24px", textAlign: "center",
                    animation: "midia-bounce-in 500ms cubic-bezier(0.23,1,0.32,1) forwards",
                    boxShadow: "0 4px 20px rgba(34,197,94,0.15)",
                  }}>
                    <div style={{ fontSize: 44, marginBottom: 10, lineHeight: 1 }}>🎉</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#15803D", letterSpacing: "-0.3px" }}>
                      ¡Todo listo por hoy!
                    </div>
                    <div style={{ fontSize: 13, color: "#4ADE80", marginTop: 6, fontWeight: 500 }}>
                      Completaste {totalCompletadas} acción{totalCompletadas !== 1 ? "es" : ""} · Bien hecho, {nombreCorto}
                    </div>
                    <button
                      onClick={() => setMostrarCierre(true)}
                      className="midia-btn-primary"
                      style={{
                        marginTop: 18, background: "linear-gradient(135deg, #22c55e, #15803D)",
                        color: "#fff", border: "none", borderRadius: 10,
                        padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                        boxShadow: "0 4px 14px rgba(34,197,94,0.35)",
                      }}
                    >
                      Ver resumen del día →
                    </button>
                  </div>
                )}

                {/* ── Clientes dormidos ── */}
                {clientesDormidos.length > 0 && (
                  <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:"14px 16px", marginTop:6 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#1e293b", marginBottom:10 }}>
                      💤 Dormidos — {clientesDormidos.length} cliente{clientesDormidos.length!==1?"s":""} sin seguimiento
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {clientesDormidos.map(cli => {
                        const en2 = new Date(Date.now() + 2*86400000);
                        const seg = `${en2.getFullYear()}-${String(en2.getMonth()+1).padStart(2,"0")}-${String(en2.getDate()).padStart(2,"0")}`;
                        return (
                          <div key={cli.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", background:"#f8fafc", borderRadius:8 }}>
                            <div style={{ width:7, height:7, borderRadius:"50%", background:"#94a3b8", flexShrink:0 }} />
                            <Link to={`/clientes/${cli.id}/perfil`} style={{ flex:1, fontSize:12, fontWeight:600, color:"#1e293b", textDecoration:"none", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {cli.nombre}{cli.empresa ? ` · ${cli.empresa}` : ""}
                            </Link>
                            <button
                              onClick={async () => {
                                await updateDoc(doc(db, "clientes", cli.id), { proximoSeguimiento: seg });
                                toast.success(`📅 Seguimiento agendado para ${cli.nombre.split(" ")[0]}`);
                              }}
                              style={{ fontSize:10, fontWeight:600, padding:"4px 9px", borderRadius:6, background:"#EEF2FF", color:"#3A3C7A", border:"none", cursor:"pointer", flexShrink:0 }}>
                              📅 Agendar
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Recompra inteligente ── */}
                {(() => {
                  const urgentes = recompraData.filter(r => !r.contactadoReciente && (r.nivelRiesgo === "inactivo" || r.nivelRiesgo === "frío")).slice(0, 4);
                  if (urgentes.length === 0) return null;
                  const ST = { inactivo: { dot:"#DC2626", bg:"#FCEBEB", color:"#A32D2D", label:"Inactivo" }, "frío": { dot:"#3B82F6", bg:"#EFF6FF", color:"#1D4ED8", label:"Frío" } };
                  return (
                    <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:"14px 16px", marginTop:6 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#1e293b", marginBottom:10 }}>
                        🔄 Recompra — {urgentes.length} cliente{urgentes.length!==1?"s":""} sin comprar
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {urgentes.map(r => {
                          const st = ST[r.nivelRiesgo] || ST["frío"];
                          return (
                            <div key={r.clienteId} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", background:"#f8fafc", borderRadius:8 }}>
                              <div style={{ width:7, height:7, borderRadius:"50%", background:st.dot, flexShrink:0 }} />
                              <Link to={`/clientes/${r.clienteId}/perfil`} style={{ flex:1, fontSize:12, fontWeight:600, color:"#1e293b", textDecoration:"none", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                {r.clienteNombre}
                              </Link>
                              <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:6, background:st.bg, color:st.color, flexShrink:0 }}>
                                {r.diasDesdeUltima}d
                              </span>
                              {r.telefono && (
                                <button onClick={() => window.open(`https://wa.me/51${r.telefono.replace(/\D/g,"")}?text=Hola ${r.clienteNombre.split(" ")[0]}, ¿cómo va el proyecto? 🏗`, "_blank")}
                                  style={{ fontSize:10, fontWeight:600, padding:"4px 9px", borderRadius:6, background:"#DCF8C6", color:"#128C7E", border:"none", cursor:"pointer", flexShrink:0 }}>
                                  WA
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

              </>
            ) : (
              clientes.length === 0 ? (
                <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                  No tienes clientes asignados aún. Agrega clientes para activar el plan diario.
                </div>
              ) : null
            )
          )}
        </>
      )}
    </div>
  );
}
